import { type NodeProps, useReactFlow } from '@xyflow/react';
import { useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { RfNodeTextNode, SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { RichTextEditorProvider } from '../../../common/rich-text-editor/RichTextEditorProvider';
import { RichTextEditor } from '../../../common/rich-text-editor/RichTextEditor';
import { RichTextToolbar } from '../../../common/rich-text-editor/RichTextToolbar';
import { CanvasNodeToolbar } from '../components/CanvasNodeToolbar';
import { NodeActionsMenu } from '../components/NodeActionsMenu';
import { useNodeRichTextEditBehavior } from '../hooks/useNodeRichTextEditBehavior';
import { useNodeDeleteOnKeyPress } from '../hooks/useNodeDeleteOnKeyPress';
import { Separator } from '../../../common/rich-text-editor/toolbar-items/Separator';
import { useRichTextEditor } from '../../../common/rich-text-editor/RichTextEditorProvider';
import { ToolbarItemType } from '../../../common/rich-text-editor/toolbar-items/toolbarItemRegistry';
import { SelectiveAspectRatioResizer } from '../components/selective-aspect-ration-resizer/SelectiveAspectRatioResizer';
import {
  SelectiveAspectRatioResizerCtxProvider,
  useSelectiveAspectRatioResizerCtx,
} from '../components/selective-aspect-ration-resizer/SelectiveAspectRatioResizerCtx';
import { CSS_Z_INDEX_NODE_HANDLE } from '../../const/zIndex';
import { TEXT_NODE_MIN_HEIGHT, TEXT_NODE_MIN_WIDTH } from './consts';
import { resizeControlStyles } from '../resizeControlStyles';
import { useGetHeightFromContentObserver } from '../hooks/useGetHeightFromContentObserver';
import { useNodeTextScaleCtx } from '../../internal-context/NodeTextScaleContext';

const PLACEHOLDER_TEXT = 'Type anything';

const styles = {
  leftToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    borderRadius: '4px',
    backgroundColor: 'white',
  },
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'top',
    justifyContent: 'top',
    borderRadius: '4px',
    '&.drop-target': {
      borderColor: colors.blue[500],
    },
    cursor: 'crosshair',
  },
} as const;

// const TEXT_NOTE_MIN_WIDTH = 10;
// const TEXT_NOTE_MIN_HEIGHT = 10;

const TEXT_NODE_TOOLBAR_ITEMS: ToolbarItemType[] = [
  'TextColor',
  'Separator',
  'FontSizeSelector',
  'Separator',
  'Bold',
  'BulletList',
  'Link',
];

const calculateNewTextScale = (
  previousWidth: number,
  newWidth: number,
  previousTextScale: number,
) => {
  return (newWidth / previousWidth) * previousTextScale;
};

const TextNode = ({
  id,
  selected,
  dragging,
  data: { richText, freshlyCreated, textScale = 1 },
  width = TEXT_NODE_MIN_WIDTH,
}: NodeProps<RfNodeTextNode>) => {
  const { setNodes } = useReactFlow<SweepCanvasRfNode>();
  const { urlEditorOpen } = useRichTextEditor();

  const {
    handleRichTextChange,
    lastEditWasEmpty,
    editMode,
    exclusivelySelected,
    setEditMode,
    multipleNodesSelected,
    saveRichText,
  } = useNodeRichTextEditBehavior({
    id,
    freshlyCreated,
    selected,
    dragging,
    trackEmptyEdits: true,
    currentRichText: richText,
  });

  useNodeDeleteOnKeyPress({
    nodeId: id,
    exclusivelySelected,
    editMode,
  });

  useEffect(() => {
    if (lastEditWasEmpty && !editMode) {
      setNodes((nodes) => nodes.filter((node) => node.id !== id));
    }
  }, [editMode, id, lastEditWasEmpty, setNodes]);

  const { isResizing, initialWidthRef, isResizingHorizontal } = useSelectiveAspectRatioResizerCtx();
  const { nodesTextScaleRef } = useNodeTextScaleCtx();

  let _textScale = textScale;
  if (isResizing && !isResizingHorizontal && initialWidthRef.current) {
    const newTextScale = calculateNewTextScale(initialWidthRef.current, width, textScale);
    nodesTextScaleRef.current.set(id, newTextScale);
    _textScale = newTextScale;
  }

  const { boxRef, observedHeight } = useGetHeightFromContentObserver(_textScale);

  const onBlur = useCallback(() => {
    saveRichText(textScale, Math.round(observedHeight));
  }, [observedHeight, saveRichText, textScale]);

  return (
    <>
      {selected && !dragging && !multipleNodesSelected && !editMode && (
        <SelectiveAspectRatioResizer
          isVisible={true}
          handleClassName="section-node-handle"
          handleStyle={resizeControlStyles.handle}
          lineStyle={resizeControlStyles.line}
          minWidth={TEXT_NODE_MIN_WIDTH}
          minHeight={TEXT_NODE_MIN_HEIGHT}
          autoScale
          defaultKeepAspectRatio={true}
          horizontalKeepAspectRatio={false}
        />
      )}
      <CanvasNodeToolbar
        className="nopan"
        align="center"
        isVisible={exclusivelySelected && !isResizing}
        hide={urlEditorOpen}
        offset={18}
      >
        <RichTextToolbar items={TEXT_NODE_TOOLBAR_ITEMS} />
        <Separator />
        <NodeActionsMenu nodeId={id} />
      </CanvasNodeToolbar>
      <Box sx={styles.outerContainer}>
        <Box
          ref={boxRef}
          sx={{
            cursor: 'text',
            width: `${width / _textScale}px`,
            scale: _textScale,
            transformOrigin: 'top left',
          }}
        >
          <RichTextEditor
            onChange={handleRichTextChange}
            initialContent={richText}
            placeholder={PLACEHOLDER_TEXT}
            autoFocus
            onBlur={onBlur}
            editable={editMode}
            hideOutline={!editMode}
          />
          {!editMode && (
            <Box
              className="drag-handle"
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: CSS_Z_INDEX_NODE_HANDLE,
                cursor: 'move',
              }}
              onClick={() => {
                if (exclusivelySelected) {
                  setEditMode(true);
                }
              }}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

const TextNodeWithProviders = (props: NodeProps<RfNodeTextNode>) => {
  return (
    <SelectiveAspectRatioResizerCtxProvider>
      <RichTextEditorProvider fontSizeSelectorScaleModifier={props.data.textScale}>
        <TextNode {...props} />
      </RichTextEditorProvider>
    </SelectiveAspectRatioResizerCtxProvider>
  );
};

export default TextNodeWithProviders;
